import type { AccessGroup } from '@/shared/access';

/**
 * Department-hierarchy helpers for the flat `AccessGroup[]` the BFF returns from `GET
 * /api/v1/access/groups` (adr-merchant-portal-016) — every group, at every depth, already ordered by
 * `path` so a parent always precedes its children and each subtree occupies a contiguous run.
 *
 * <p>That ordering guarantee is what lets every function here work in a single pass over the flat
 * list instead of first rebuilding a nested tree — the Groups table, and the parent pickers, all want
 * the flat, ordered shape anyway.
 */

/** `depth`, defaulting to top-level (`0`) for a group predating the hierarchy. */
function depthOf(group: Pick<AccessGroup, 'depth'>): number {
  return group.depth ?? 0;
}

/** The id of every group that has at least one child — i.e. every row that can be collapsed. */
export function groupsWithChildren(groups: AccessGroup[]): Set<string> {
  const withChildren = new Set<string>();
  for (const group of groups) {
    if (group.parentId) withChildren.add(group.parentId);
  }
  return withChildren;
}

/**
 * The rows to actually render given which ids are collapsed. Relies on the BFF's ordering guarantee
 * (parent before child, subtree contiguous): once a collapsed row is seen, every following row deeper
 * than it is skipped, until a row at or above its own depth reappears — that row is either a sibling or
 * an ancestor's sibling, i.e. the collapsed subtree has ended.
 */
export function visibleGroupRows(groups: AccessGroup[], collapsedIds: Set<string>): AccessGroup[] {
  const visible: AccessGroup[] = [];
  let suppressBelowDepth: number | null = null;

  for (const group of groups) {
    const depth = depthOf(group);
    if (suppressBelowDepth !== null && depth > suppressBelowDepth) {
      continue;
    }
    suppressBelowDepth = null;
    visible.push(group);
    if (collapsedIds.has(group.id)) {
      suppressBelowDepth = depth;
    }
  }

  return visible;
}

/**
 * Every descendant of `groupId`, at any depth — used for the cascading-delete warning's counts (how
 * many sub-departments, and how many of their direct members, are deleted along with the group).
 */
export function descendantsOf(groups: AccessGroup[], groupId: string): AccessGroup[] {
  const rootIndex = groups.findIndex((group) => group.id === groupId);
  if (rootIndex === -1) return [];
  const rootDepth = depthOf(groups[rootIndex]);

  const descendants: AccessGroup[] = [];
  for (let i = rootIndex + 1; i < groups.length; i += 1) {
    const group = groups[i];
    if (depthOf(group) <= rootDepth) break; // contiguous subtree has ended
    descendants.push(group);
  }
  return descendants;
}

/**
 * Parent-selector options: every group, in the BFF's path order (so nesting reads top-to-bottom) —
 * minus, when moving an existing group (`excludeId`), that group and its whole subtree, since it can
 * never become its own descendant's parent. The BFF is the final authority (`400` on a cycle); this
 * only avoids offering a choice that would merely round-trip to that error, mirroring
 * `catalog/lib/categoryTree.ts#buildParentOptions`.
 */
export function groupParentOptions(groups: AccessGroup[], excludeId?: string): AccessGroup[] {
  if (!excludeId) return groups;
  const excluded = new Set([
    excludeId,
    ...descendantsOf(groups, excludeId).map((group) => group.id),
  ]);
  return groups.filter((group) => !excluded.has(group.id));
}

/**
 * A group's `path` rendered as a legible breadcrumb (`"/Sales/EMEA"` → `"Sales / EMEA"`), falling back
 * to the bare name for a group predating the hierarchy (no `path` yet). Names are unique among siblings
 * only, so the path — not the name alone — is what disambiguates two same-named groups under different
 * parents in a picker.
 */
export function formatGroupPath(group: Pick<AccessGroup, 'name' | 'path'>): string {
  if (!group.path) return group.name;
  const segments = group.path.split('/').filter(Boolean);
  return segments.length > 0 ? segments.join(' / ') : group.name;
}
